import axios, { AxiosError, AxiosResponse, CancelTokenSource } from "axios";
import { ResponseData } from "../models/data";
import { FullCase, RecentCaseReference, SummaryCase } from "../models/case";
import { User, UserNameAddress } from "../models/user";
import { ACD, Attachment, Classification, Team, TeamAccess, SettingsLog, MailboxFolder } from "../models";
import {
  apiUrl,
  axiosConfig,
  handleError,
  showError,
} from "./shared/dataServiceHelpers";
import { Group } from "../models/group";
import { PollingAddress, PollingAddressStatus } from "../models/pollingAddress";
import { IncomingEmailAddressRule } from "../models/incomingEmailAddressRule";
import { getToken } from "./auth.service";
import { SelectOption } from "../models/selectOption";
import { TransferFormsReport } from "../models/transferFormsReport";
import { MutableRefObject } from "react";
import { StaticData } from "../models/staticData";

const getDataByUrl = (url: string, cancelToken: MutableRefObject<CancelTokenSource|null>|null = null): Promise<ResponseData<unknown>> => {
  const requestConfig = axiosConfig();
  if (cancelToken) {
    if (cancelToken.current) {
      cancelToken.current.cancel();
    }
    cancelToken.current = axios.CancelToken.source();
    requestConfig.cancelToken = cancelToken.current.token;
  }
  return axios
    .get(`${apiUrl}${url}`, requestConfig)
    .then((axiosRes: AxiosResponse) => {
      const response: ResponseData<unknown> = axiosRes?.data;
      if (typeof response === "object" && !response.success) {
        showError(response.message || "Unknown error while getting data");
      }
      return response as ResponseData<unknown>;
    })
    .catch((error: AxiosError) => {
      if (!axios.isCancel(error)) {        
        handleError(error);
      }
      return { message: error.message, success: false } as ResponseData<null>;
    });
};

export const getACDs = (): Promise<ResponseData<ACD[]>> => {
  return getDataByUrl("acds/getall") as Promise<ResponseData<ACD[]>>;
};

export const getActionClassifyEmail = (): 
Promise<ResponseData<SelectOption[]>> => {
  return getDataByUrl("actionclassifyemail/getall") as
    Promise<ResponseData<SelectOption[]>>;
};

export const getActionExtractAccountData = (): 
Promise<ResponseData<SelectOption[]>> => {
  return getDataByUrl("actionextractaccountdata/getoptions") as
    Promise<ResponseData<SelectOption[]>>;
};

export const getActionExtractTransferFormData = (): 
Promise<ResponseData<SelectOption[]>> => {
  return getDataByUrl("actionextracttransferformdata/getoptions") as
    Promise<ResponseData<SelectOption[]>>;
};

export const getDropdownData = (
  path: string
): Promise<ResponseData<SelectOption[]>> => {
  return getDataByUrl(`${path}/getoptions`) as Promise<ResponseData<SelectOption[]>>;
};

export const getCases = (
  queryString: string,
  cancelToken: MutableRefObject<CancelTokenSource|null> | null = null
): Promise<ResponseData<SummaryCase[]>> => {
  const url = `teamq/getcases?${queryString}`;
  return getDataByUrl(url, cancelToken) as Promise<ResponseData<SummaryCase[]>>;
};

export const getCaseCount = (
  pageName: string,
  filterQueryString: string,
  cancelToken: MutableRefObject<CancelTokenSource|null> | null = null
): Promise<ResponseData<number>> => {
  const url = `teamq/getcasecount?filterBy=${filterQueryString}&pageName=${pageName}`;
  return getDataByUrl(url, cancelToken) as Promise<ResponseData<number>>;
};

export const getActiveUsers = (): Promise<ResponseData<UserNameAddress[]>> => {
  return getDataByUrl("users/getactiveusers") as Promise<ResponseData<UserNameAddress[]>>;
};

export const getAllUsers = (): Promise<ResponseData<User[]>> => {
  return getDataByUrl("users/getallusers") as Promise<ResponseData<User[]>>;
};

export const getAttachment = (
  type: string,
  attachmentId: number
): Promise<ResponseData<Attachment>> => {
  return getDataByUrl(`attachments/getattachment/${type}/${attachmentId}`) as Promise<ResponseData<Attachment>>;
};

export const getAttachmentContent = (
  type: string,
  attachmentId: number,
): Promise<Blob> => {
  return axios
    .get(`${apiUrl}attachments/getattachmentcontent/${type}/${attachmentId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
      responseType: 'blob'
    })
    .then((response) => {
      return response.data;
    });
};

export const getAttachments = (
  caseId: string
): Promise<ResponseData<unknown>> => {
  return getDataByUrl(`cases/getcaseattachments/${caseId}`);
};

export const getCasesById = (id: string): Promise<ResponseData<FullCase>> => {
  const url = `cases/getcase/${id}`;
  return getDataByUrl(url) as Promise<ResponseData<FullCase>>;
};

export const getCaseEmail = (
  caseId: string
): Promise<ResponseData<unknown>> => {
  return getDataByUrl(`cases/getcaseemails/${caseId}`);
};

export const getAllClassifications = (): Promise<ResponseData<Classification[]>> => {
  return getDataByUrl(`classifications/getall`) as Promise<
    ResponseData<Classification[]>
  >;
};

export const getClassificationsForCases = (
  selectedCases: string[]
): Promise<ResponseData<Classification[]>> => {
  return getDataByUrl(`classifications/getforcases/${selectedCases.join(",")}`) as Promise<
    ResponseData<Classification[]>
  >;
};

export const getDashboardData = (
  queryString: string
): Promise<ResponseData<any>> => {
  return getDataByUrl(`dashboard/getstats?${queryString}`);
};

export const getFilters = (): Promise<any> => {
  return getDataByUrl("users/getmyfilters");
};

export const getGroups = (): Promise<ResponseData<Group[]>> => {
  return getDataByUrl("groups/getgroups") as Promise<ResponseData<Group[]>>;
};

export const getGroupOptionsForGroupRules = (): Promise<ResponseData<SelectOption[]>> => {
  return getDataByUrl("groups/getoptionsforgrouprules") as Promise<ResponseData<SelectOption[]>>;
};

export const getGroupIdsForTeams = (
  teamIds: number[]
): Promise<ResponseData<number[]>> => {
  return getDataByUrl(`teams/getgroupidsforteams/${teamIds.join(",")}`) as Promise<ResponseData<number[]>>;
};

export const getMailboxFolders = (
  emailAddress: string
): Promise<ResponseData<MailboxFolder[]>> => {
  return getDataByUrl(`emailaccounts/getmailboxfolders/${encodeURIComponent(emailAddress.trim())}`) as Promise<ResponseData<MailboxFolder[]>>;
};

export const getGroupOptionsForResend = (
  caseId: number
): Promise<ResponseData<SelectOption[]>> => {
  return getDataByUrl(`groups/getgroupoptionsforresend/${caseId}`) as Promise<ResponseData<SelectOption[]>>;
};

export const getIncomingEmailAddressRules = (): Promise<
  ResponseData<IncomingEmailAddressRule[]>
> => {
  return getDataByUrl("emailaccounts/getincomingemailaddressrules") as Promise<
    ResponseData<IncomingEmailAddressRule[]>
  >;
};

export const getIncomingEmailAddressRuleById = (ruleId: number): Promise<ResponseData<IncomingEmailAddressRule>> => {
  return getDataByUrl(`emailaccounts/getincomingemailaddressrule/${ruleId}`) as Promise<
    ResponseData<IncomingEmailAddressRule>
  >;
};

export const getPollingAddresses = (): Promise<
  ResponseData<PollingAddressStatus[]>
> => {
  return getDataByUrl("emailaccounts/getpollingstatus") as Promise<
    ResponseData<PollingAddressStatus[]>
  >;
};

export const getPollingAddressesForUser = (): Promise<ResponseData<PollingAddress[]>> => {
  return getDataByUrl(`emailaccounts/getpollingaddressesforuser`) as Promise<
    ResponseData<PollingAddress[]>
  >;
};

export const getPollingAddressesForFolders = (): Promise<ResponseData<string[]>> => {
  return getDataByUrl("emailaccounts/getforfolders") as Promise<ResponseData<string[]>>;
};


export const getRecentCases = (): Promise<
  ResponseData<RecentCaseReference[]>
> => {
  return getDataByUrl("users/getmyrecentlyviewed") as Promise<
    ResponseData<RecentCaseReference[]>
  >;
};

export const getNewTransferFormsReport = (): Promise<
  ResponseData<TransferFormsReport>
> => {
  return getDataByUrl("reports/gettransferformsreport") as Promise<
    ResponseData<TransferFormsReport>
  >;
}

export const getExistingTransferFormsReport = (
  id: number
): Promise<
  ResponseData<TransferFormsReport>
> => {
  return getDataByUrl(`reports/gettransferformsreport/${id}`) as Promise<
    ResponseData<TransferFormsReport>
  >;
}

export const getTransferFormsReports = (): Promise<
  ResponseData<TransferFormsReport[]>
> => {
  return getDataByUrl("reports/getalltransferformsreports") as Promise<
    ResponseData<TransferFormsReport[]>
  >;
} 
export const getSettings = <T>(path: string): Promise<ResponseData<T[]>> => {
  return getDataByUrl(`${path}/getall`) as Promise<ResponseData<T[]>>;
}; 

export const getTeamQStaticData = (): Promise<ResponseData<StaticData>> => {
  return getDataByUrl(`teamq/getstaticdata`) as Promise<
    ResponseData<StaticData>
  >;
};

export const GetTeamAccesses = (): Promise<ResponseData<TeamAccess[]>> => {
  return getDataByUrl("teamaccesses/getteamaccesses") as Promise<
    ResponseData<TeamAccess[]>
  >;
};

export const getTeams = (): Promise<ResponseData<Team[]>> => {
  return getDataByUrl("teams/getteams") as Promise<ResponseData<Team[]>>;
};

export const getTeamsForGroups = (
  groupIds: number[]
): Promise<ResponseData<Team[]>> => {
  return getDataByUrl(`teams/getteamsforgroups/${groupIds.join(",")}`) as Promise<ResponseData<Team[]>>;
};

export const getTimezones = (): Promise<ResponseData<string[]>> => {
  return getDataByUrl("timezone/getall") as Promise<ResponseData<string[]>>; 
};
export const getUsersByTeam = (
  teamId: number
): Promise<ResponseData<User>> => {
  return getDataByUrl(`users/getusersbyteam/${teamId}`) as Promise<
    ResponseData<User>
  >;
}; 

export const upsertLogin = (): Promise<ResponseData<User>> => {
  return axios
    .get(`${apiUrl}authentication/login`, axiosConfig())
    .then((res) => {
      return res.data;
    })
    .catch(handleError);
};

export const getSettingsLogsByEntityName = (
  entityName: string, 
  pageIndex = 0, 
  pageSize = 20
): Promise<ResponseData<SettingsLog[]>> => {
  const url = `settingslogs/getbyentityname/${entityName}?pageIndex=${pageIndex}&pageSize=${pageSize}`;
  return getDataByUrl(url) as Promise<ResponseData<SettingsLog[]>>;
};
